import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  AfterViewInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoFocusModule } from 'primeng/autofocus';

@Component({
  selector: 'app-pin-input',
  standalone: true,
  imports: [CommonModule, FormsModule, AutoFocusModule],
  templateUrl: './pin-input.component.html',
  styleUrls: ['./pin-input.component.scss'],
})
export class PinInputComponent implements AfterViewInit, OnDestroy {
  private readonly PIN_LENGTH = 6;
  private readonly EMPTY_PIN = Array(this.PIN_LENGTH).fill('');

  pin: string[] = [...this.EMPTY_PIN];
  displayPin: string[] = [...this.EMPTY_PIN];
  @Input() loading = false;
  @Input() hasError = false;
  @Input() showToggleVisibility = true;
  @Output() pinReady = new EventEmitter<string>();
  @Output() isPinValid = new EventEmitter<boolean>();
  validPin: boolean = false;
  showPin: boolean = false;

  @ViewChildren('pinInput') pinInputElements!: QueryList<ElementRef>;
  private readonly inputListeners = new Map<
    ElementRef,
    {
      input?: (e: Event) => void;
      paste?: (e: ClipboardEvent) => void;
      keydown?: (e: KeyboardEvent) => void;
    }
  >();

  ngAfterViewInit(): void {
    this.attachInputListeners();
  }

  private attachInputListeners(): void {
    this.pinInputElements.forEach((element, index) => {
      const inputElement = element.nativeElement;

      const handleInputFn = () => this.handleInput(inputElement, index);
      const handlePasteFn = (event: ClipboardEvent) =>
        this.handlePaste(event, index);
      const handleKeyDownFn = (event: KeyboardEvent) =>
        this.handleKeyDown(event, index);

      inputElement.addEventListener('input', handleInputFn);
      inputElement.addEventListener('paste', handlePasteFn);
      inputElement.addEventListener('keydown', handleKeyDownFn);

      this.inputListeners.set(element, {
        input: handleInputFn,
        paste: handlePasteFn,
        keydown: handleKeyDownFn,
      });
    });
  }

  handleInput(inputElement: HTMLInputElement, index: number): void {
    let inputValue = inputElement.value;
    inputValue = inputValue.replace(/\D/g, '').substring(0, 1);
    this.pin[index] = inputValue;

    this.updateDisplayPin();

    inputElement.value = this.displayPin[index];

    if (inputValue && index < this.PIN_LENGTH - 1) {
      const nextInput = this.pinInputElements.get(index + 1)?.nativeElement;
      if (nextInput) {
        this.focusAndSelectInput(nextInput);
      }
    }
    this.checkPinValidity();
  }

  private focusAndSelectInput(inputElement: HTMLInputElement): void {
    inputElement.focus();
    inputElement.select();
  }

  private updateDisplayPin(): void {
    this.displayPin = this.pin.map((digit) => {
      if (!digit) return '';
      return this.showPin ? digit : '*';
    });

    this.pinInputElements?.forEach((element, i) => {
      element.nativeElement.value = this.displayPin[i];
    });
  }

  handleKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && index > 0 && !this.pin[index]) {
      const prevInput = this.pinInputElements.get(index - 1)?.nativeElement;
      if (prevInput) {
        this.focusAndSelectInput(prevInput);
      }
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      this.handlePasteFromClipboard(index);
      event.preventDefault();
    }
    this.preventInvalidInput(event);
  }

  preventInvalidInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent, index: number) {
    const pastedData = event.clipboardData?.getData('text') || '';
    this.processPastedData(pastedData, index);
    event.preventDefault();
  }

  private async handlePasteFromClipboard(index: number) {
    try {
      const clipboardText = await navigator.clipboard.readText();
      this.processPastedData(clipboardText, index);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  }

  private processPastedData(data: string, startIndex: number): void {
    const sanitizedData = data.replace(/\D/g, '');
    if (!sanitizedData) return;

    for (
      let i = 0;
      i < sanitizedData.length && startIndex + i < this.PIN_LENGTH;
      i++
    ) {
      this.pin[startIndex + i] = sanitizedData[i];
    }

    this.updateDisplayPin();
    this.focusNextEmptyInput();
    this.checkPinValidity();
  }

  private focusNextEmptyInput(): void {
    setTimeout(() => {
      const nextEmptyIndex = this.pin.findIndex((val) => !val);
      const focusIndex =
        nextEmptyIndex === -1 ? this.PIN_LENGTH - 1 : nextEmptyIndex;

      if (focusIndex < this.pinInputElements.length) {
        const inputToFocus = this.pinInputElements.get(focusIndex);
        if (inputToFocus) {
          this.focusAndSelectInput(inputToFocus.nativeElement);
        }
      }
    }, 0);
  }

  cleanInput() {
    this.clearPin();
    this.checkPinValidity();
  }

  private clearPin(): void {
    this.pin = [...this.EMPTY_PIN];
    this.displayPin = [...this.EMPTY_PIN];
    this.pinInputElements.forEach((element) => {
      element.nativeElement.value = '';
    });
  }

  checkPinValidity() {
    this.validPin = this.pin.every((val) => val.length > 0);
    if (this.validPin) {
      this.pinReady.emit(this.pin.join(''));
    }
    this.isPinValid.emit(this.validPin);
  }

  toggleShowPin(): void {
    this.showPin = !this.showPin;
    this.updateDisplayPin();
  }

  ngOnDestroy(): void {
    this.inputListeners.forEach((listeners, element) => {
      const inputElement = element.nativeElement;
      if (listeners.input) {
        inputElement.removeEventListener('input', listeners.input);
      }
      if (listeners.paste) {
        inputElement.removeEventListener('paste', listeners.paste);
      }
      if (listeners.keydown) {
        inputElement.removeEventListener('keydown', listeners.keydown);
      }
    });
    this.inputListeners.clear();
  }
}
